import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { ChevronLeft, MapPin, Check, X, Phone, Package } from 'lucide-react';
import { toast } from 'sonner';

import {
  formatRupees,
  ORDER_STATUS_LABELS,
  ORDER_PROGRESSION,
  UNIT_LABELS,
} from '@sreerasa/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrder, useCancelOrder } from '@/lib/queries';
import { supabase } from '@/lib/supabase';
import { cn, formatDateTime } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';
import { STATUS_VARIANT } from './index';

export const Route = createFileRoute('/orders/$id')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: '/auth/sign-in' });
  },
  component: OrderDetailPage,
});

function OrderDetailPage() {
  const { id } = Route.useParams();
  const { data: order, isLoading } = useOrder(id);
  const cancelOrder = useCancelOrder();
  const [isCancelling, setIsCancelling] = useState(false);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 rounded-card" />
        <Skeleton className="h-64 rounded-card" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <h1 className="font-display text-2xl font-bold text-soil-900">Order not found</h1>
        <p className="mt-2 text-soil-600">This order doesn't exist, or it isn't yours.</p>
        <Link to="/orders" className="mt-6 inline-block">
          <Button>Back to orders</Button>
        </Link>
      </div>
    );
  }

  // Mirrors what RLS actually permits (`customers cancel own pending orders`,
  // migration 0500). Showing the button beyond this point would just produce
  // a failed request.
  const canCancel = order.status === 'pending_confirmation' || order.status === 'confirmed';
  const isDead = order.status === 'cancelled' || order.status === 'refunded';
  const currentStep = ORDER_PROGRESSION.indexOf(order.status);

  async function handleCancel() {
    if (!isCancelling) {
      setIsCancelling(true);
      return;
    }
    try {
      await cancelOrder.mutateAsync({ id, reason: 'Cancelled by customer' });
      toast.success('Order cancelled', { description: 'The stock has gone back on the shelf.' });
    } catch (error) {
      toast.error('Could not cancel', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsCancelling(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <Link
        to="/orders"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-soil-500 transition-colors hover:text-farm-700"
      >
        <ChevronLeft className="size-4" aria-hidden />
        All orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-soil-900">{order.order_number}</h1>
          <p className="mt-1 text-sm text-soil-500">Placed {formatDateTime(order.placed_at)}</p>
        </div>
        <Badge variant={STATUS_VARIANT[order.status]} size="lg">
          {ORDER_STATUS_LABELS[order.status]}
        </Badge>
      </div>

      {/* ── Tracker ── */}
      {!isDead && (
        <div className="mt-8 rounded-card border border-soil-200 bg-white p-6">
          <div className="relative">
            {/* Rail */}
            <div className="absolute left-0 right-0 top-4 h-0.5 bg-soil-200" aria-hidden />
            <motion.div
              className="absolute left-0 top-4 h-0.5 bg-farm-600"
              initial={{ width: 0 }}
              animate={{
                width: `${(currentStep / (ORDER_PROGRESSION.length - 1)) * 100}%`,
              }}
              transition={{ ...transitions.slow, delay: 0.2 }}
              aria-hidden
            />

            <ol className="relative flex justify-between">
              {ORDER_PROGRESSION.map((step, index) => {
                const isDone = index <= currentStep;
                return (
                  <li key={step} className="flex flex-col items-center gap-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ ...transitions.spring, delay: 0.1 + index * 0.08 }}
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full ring-4 ring-white transition-colors',
                        isDone ? 'bg-farm-600 text-white' : 'bg-soil-200 text-soil-400',
                      )}
                    >
                      {isDone ? (
                        <Check className="size-4" aria-hidden />
                      ) : (
                        <span className="size-2 rounded-full bg-current" />
                      )}
                    </motion.div>
                    <span
                      className={cn(
                        'max-w-16 text-center text-[10px] font-medium leading-tight sm:max-w-none sm:text-xs',
                        isDone ? 'text-soil-900' : 'text-soil-400',
                      )}
                    >
                      {ORDER_STATUS_LABELS[step]}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}

      {isDead && order.cancellation_reason && (
        <div className="mt-8 rounded-card border border-red-200 bg-red-50 p-5">
          <p className="flex items-center gap-2 font-medium text-danger">
            <X className="size-4" aria-hidden />
            {order.status === 'cancelled' ? 'Cancelled' : 'Refunded'}
          </p>
          <p className="mt-1 text-sm text-soil-600">{order.cancellation_reason}</p>
        </div>
      )}

      {/* ── Items ── */}
      <div className="mt-6 rounded-card border border-soil-200 bg-white p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-soil-900">
          <Package className="size-4 text-farm-600" aria-hidden />
          What's in it
        </h2>

        <ul className="mt-4 divide-y divide-soil-100">
          {order.order_items?.map((item) => (
            <li key={item.id} className="flex justify-between gap-4 py-3">
              <div className="min-w-0">
                {/* These are snapshots, taken at order time. If the farmer
                    later renames the listing or it's delisted entirely, this
                    still says what the customer actually bought. */}
                <p className="font-medium text-soil-900">{item.title_snapshot}</p>
                {item.farm_name_snapshot && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-soil-500">
                    <MapPin className="size-3" aria-hidden />
                    {item.farm_name_snapshot}
                    {item.village_snapshot && `, ${item.village_snapshot}`}
                    {item.farmer_name_snapshot && ` · ${item.farmer_name_snapshot}`}
                  </p>
                )}
                <p className="mt-1 text-xs text-soil-500">
                  {item.quantity} {UNIT_LABELS[item.unit_snapshot]} ×{' '}
                  {formatRupees(item.unit_price_paise)}
                </p>
              </div>
              <p className="shrink-0 font-display font-bold tabular-nums text-soil-900">
                {formatRupees(item.line_total_paise)}
              </p>
            </li>
          ))}
        </ul>

        <dl className="mt-4 space-y-2 border-t border-soil-200 pt-4 text-sm">
          <div className="flex justify-between">
            <dt className="text-soil-600">Subtotal</dt>
            <dd className="tabular-nums">{formatRupees(order.subtotal_paise)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-soil-600">Delivery</dt>
            <dd className="tabular-nums">
              {order.delivery_fee_paise === 0 ? (
                <span className="text-farm-600">Free</span>
              ) : (
                formatRupees(order.delivery_fee_paise)
              )}
            </dd>
          </div>
          <div className="flex justify-between border-t border-soil-100 pt-2 font-display text-base font-bold text-soil-900">
            <dt>Total</dt>
            <dd className="tabular-nums">{formatRupees(order.total_paise)}</dd>
          </div>
        </dl>
      </div>

      {/* ── Delivery ── */}
      <div className="mt-6 rounded-card border border-soil-200 bg-white p-5">
        <h2 className="font-display text-lg font-bold text-soil-900">Delivering to</h2>
        <div className="mt-3 text-sm leading-relaxed text-soil-700">
          <p className="font-medium text-soil-900">{order.delivery_name}</p>
          <p>
            {[order.delivery_line1, order.delivery_line2, order.delivery_landmark]
              .filter(Boolean)
              .join(', ')}
          </p>
          <p>
            {order.delivery_city}, {order.delivery_state} {order.delivery_pincode}
          </p>
          <p className="mt-2 flex items-center gap-1.5 text-soil-500">
            <Phone className="size-3.5" aria-hidden />
            {order.delivery_phone}
          </p>
          {order.delivery_slot && (
            <p className="mt-2 text-soil-600">
              <span className="font-medium">Slot:</span> {order.delivery_slot}
            </p>
          )}
          {order.delivery_notes && (
            <p className="mt-1 text-soil-600">
              <span className="font-medium">Notes:</span> {order.delivery_notes}
            </p>
          )}
        </div>
      </div>

      {canCancel && (
        <div className="mt-6 flex justify-end">
          {/* Two-step confirm. A one-click cancel on a real order is a
              mis-tap waiting to happen. */}
          <Button
            variant={isCancelling ? 'danger' : 'ghost'}
            onClick={handleCancel}
            isLoading={cancelOrder.isPending}
          >
            {isCancelling ? 'Yes, cancel this order' : 'Cancel order'}
          </Button>
          {isCancelling && (
            <Button variant="ghost" onClick={() => setIsCancelling(false)}>
              Keep it
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
